import { useEffect, useState } from "react";

/**
 * Where the sidebar stops being a column and becomes a drawer.
 *
 * Below this width there is no room for a rail next to the content, so the same
 * `<aside>` slides in over the page from a button in the header. The query is
 * mirrored by the `max-width: 760px` block in index.css: the CSS moves the
 * panel, these hooks decide who opens it. Change one, change the other.
 */
export const NAV_DRAWER_MAX = 760;
export const NAV_DRAWER_MQ = `(max-width: ${NAV_DRAWER_MAX}px)`;

/** True while the viewport is in drawer territory. */
export function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(NAV_DRAWER_MQ).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(NAV_DRAWER_MQ);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return narrow;
}

/**
 * The drawer's open state, with the three ways out that a menu over the page
 * needs: a route change, Escape, and growing back to a desktop width. The last
 * one matters most - a drawer left open while the rail reappears would leave a
 * panel floating over a sidebar that is already there.
 */
export function useNavDrawer(narrow: boolean, pathname: string): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!narrow) setOpen(false);
  }, [narrow]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return [open && narrow, setOpen];
}
